import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Plus, CreditCard, FileText, TrendingUp, AlertCircle, Eye, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import InvoiceViewer from './InvoiceViewer';
import ReceiptViewer from './ReceiptViewer';

const API = process.env.REACT_APP_BACKEND_URL || '';

const FeesManagement = ({ user }) => {
  const [students, setStudents] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [feeStructure, setFeeStructure] = useState(null);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  const [invoiceFormData, setInvoiceFormData] = useState({
    student_id: '',
    student_mode: 'offline',
    fee_frequency: 'monthly',
    due_date: '',
    notes: ''
  });

  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    payment_method: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    transaction_id: '',
    cheque_number: '',
    notes: ''
  });

  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [showBulkResultDialog, setShowBulkResultDialog] = useState(false);

  // Invoice and Receipt Viewer states
  const [selectedInvoiceForView, setSelectedInvoiceForView] = useState(null);
  const [selectedReceiptForView, setSelectedReceiptForView] = useState(null);
  const [isInvoiceViewerOpen, setIsInvoiceViewerOpen] = useState(false);
  const [isReceiptViewerOpen, setIsReceiptViewerOpen] = useState(false);

  // Financial summary states
  const [financialSummary, setFinancialSummary] = useState({
    pendingToHeadOffice: 0,
    toCollectThisMonth: 0,
    totalCollectedThisMonth: 0,
    totalOutstanding: 0
  });

  // Kit-related financial data
  const [kitRequests, setKitRequests] = useState([]);
  const [kitFinancialData, setKitFinancialData] = useState({
    totalKitCollectible: 0,
    totalKitPayable: 0,
    pendingKitPayments: 0,
    collectedKitPayments: 0,
    royaltyDue: 0,
    royaltyPending: 0
  });

  useEffect(() => {
    loadStudents();
    loadInvoices();
    loadPayments();
    loadFeeStructure();
    loadKitRequests();
  }, []);

  useEffect(() => {
    calculateFinancialSummary();
  }, [invoices, payments]);

  useEffect(() => {
    calculateKitFinancialData();
  }, [kitRequests, invoices]);

  const loadStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/users?role=student&center_id=${user.center_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data);
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Failed to load students');
    }
  };

  const loadInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/fee-invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Failed to load invoices');
    }
  };

  const loadPayments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data);
    } catch (error) {
      console.error('Error loading payments:', error);
      toast.error('Failed to load payments');
    }
  };

  const loadFeeStructure = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/fee-structures`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.length > 0) {
        setFeeStructure(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to load fee structure', error);
    }
  };

  const loadKitRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/kit-requests?center_id=${user.center_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKitRequests(response.data);
    } catch (error) {
      console.error('Error fetching kit requests:', error);
    }
  };

  const calculateKitFinancialData = () => {
    // Calculate kit financials
    const collectibleKits = kitRequests.filter(k => k.status === 'approved' && k.payment_status === 'pending');
    const paidKits = kitRequests.filter(k => k.payment_status === 'paid');
    const totalKitCollectible = collectibleKits.reduce((sum, kit) => sum + (kit.kit_amount || 0), 0);
    const collectedKitPayments = paidKits.reduce((sum, kit) => sum + (kit.kit_amount || 0), 0);
    
    // Calculate payable to head office (kit cost to Geniuskid)
    const totalKitPayable = kitRequests.filter(k => k.status === 'approved')
      .reduce((sum, kit) => sum + ((kit.kit_amount || 0) * 0.7), 0); // Assuming 70% goes to head office

    // Calculate royalty from invoices
    const paidInvoices = invoices.filter(inv => inv.status === 'paid');
    const pendingInvoices = invoices.filter(inv => inv.status === 'pending');
    const royaltyDue = paidInvoices.reduce((sum, inv) => sum + (inv.royalty_amount || 0), 0);
    const royaltyPending = pendingInvoices.reduce((sum, inv) => sum + (inv.royalty_amount || 0), 0);

    setKitFinancialData({
      totalKitCollectible: totalKitCollectible,
      totalKitPayable: totalKitPayable,
      pendingKitPayments: collectibleKits.length,
      collectedKitPayments: collectedKitPayments,
      royaltyDue: royaltyDue,
      royaltyPending: royaltyPending
    });
  };

  const calculateFinancialSummary = () => {
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    // Calculate pending to head office (total royalty from paid invoices)
    const pendingToHO = paidInvoices.reduce((sum, inv) => sum + (inv.royalty_amount || 0), 0);

    // Calculate to collect this month (pending invoices from current month)
    const toCollect = pendingInvoices
      .filter(inv => {
        const invDate = new Date(inv.created_at);
        return invDate.getMonth() + 1 === currentMonth && invDate.getFullYear() === currentYear;
      })
      .reduce((sum, inv) => sum + inv.amount, 0);

    // Calculate total collected this month
    const collectedThisMonth = payments
      .filter(payment => {
        const paymentDate = new Date(payment.payment_date);
        return paymentDate.getMonth() + 1 === currentMonth && paymentDate.getFullYear() === currentYear;
      })
      .reduce((sum, payment) => sum + payment.amount, 0);

    // Calculate total outstanding (all pending invoices)
    const totalOutstanding = pendingInvoices.reduce((sum, inv) => sum + inv.amount, 0);

    setFinancialSummary({
      pendingToHeadOffice: pendingToHO,
      toCollectThisMonth: toCollect,
      totalCollectedThisMonth: collectedThisMonth,
      totalOutstanding: totalOutstanding
    });
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/fee-invoices`, invoiceFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice created successfully');
      setIsInvoiceDialogOpen(false);
      setInvoiceFormData({
        student_id: '',
        student_mode: 'offline',
        fee_frequency: 'monthly',
        due_date: '',
        notes: ''
      });
      loadInvoices();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...paymentFormData,
        invoice_id: selectedInvoice.id,
        amount: parseFloat(paymentFormData.amount)
      };
      
      await axios.post(`${API}/api/payments`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment recorded successfully');
      setIsPaymentDialogOpen(false);
      setSelectedInvoice(null);
      setPaymentFormData({
        amount: '',
        payment_method: 'cash',
        payment_date: new Date().toISOString().split('T')[0],
        transaction_id: '',
        cheque_number: '',
        notes: ''
      });
      loadInvoices();
      loadPayments();
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentFormData({
      ...paymentFormData,
      amount: invoice.amount.toString()
    });
    setIsPaymentDialogOpen(true);
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    return student?.name || studentId;
  };

  const handleBulkGenerate = async () => {
    try {
      setIsBulkGenerating(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/api/fee-invoices/bulk-generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBulkResult(response.data);
      setShowBulkResultDialog(true);
      toast.success(`Generated ${response.data.invoices_generated} invoices successfully!`);
      
      // Reload invoices to show newly generated ones
      loadInvoices();
    } catch (error) {
      console.error('Bulk generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate invoices');
    } finally {
      setIsBulkGenerating(false);
    }
  };

  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1];
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoiceForView(invoice);
    setIsInvoiceViewerOpen(true);
  };

  const handleViewReceipt = (payment) => {
    setSelectedReceiptForView(payment);
    setIsReceiptViewerOpen(true);
  };

  const pendingInvoices = invoices.filter(inv => inv.status === 'pending' || inv.status === 'overdue');
  const paidInvoices = invoices.filter(inv => inv.status === 'paid');

  return (
    <div className="space-y-6">
      {/* Financial Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium">Pending to Head Office</p>
              <p className="text-2xl font-bold text-red-700 mt-2">₹{financialSummary.pendingToHeadOffice.toFixed(2)}</p>
              <p className="text-xs text-red-500 mt-1">Royalty Amount</p>
            </div>
            <div className="w-12 h-12 bg-red-200 rounded-full flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">To Collect This Month</p>
              <p className="text-2xl font-bold text-orange-700 mt-2">₹{financialSummary.toCollectThisMonth.toFixed(2)}</p>
              <p className="text-xs text-orange-500 mt-1">Current Month Pending</p>
            </div>
            <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Collected This Month</p>
              <p className="text-2xl font-bold text-green-700 mt-2">₹{financialSummary.totalCollectedThisMonth.toFixed(2)}</p>
              <p className="text-xs text-green-500 mt-1">Payments Received</p>
            </div>
            <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium">Total Outstanding</p>
              <p className="text-2xl font-bold text-purple-700 mt-2">₹{financialSummary.totalOutstanding.toFixed(2)}</p>
              <p className="text-xs text-purple-500 mt-1">All Pending Invoices</p>
            </div>
            <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Kit & Royalty Financial Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="p-5 bg-gradient-to-br from-green-50 to-emerald-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Kit Collectible</p>
              <p className="text-2xl font-bold text-green-700 mt-2">₹{kitFinancialData.totalKitCollectible.toFixed(2)}</p>
              <p className="text-xs text-green-500 mt-1">From Students</p>
            </div>
            <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-red-50 to-rose-100 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium">Kit Payable</p>
              <p className="text-2xl font-bold text-red-700 mt-2">₹{kitFinancialData.totalKitPayable.toFixed(2)}</p>
              <p className="text-xs text-red-500 mt-1">To Head Office</p>
            </div>
            <div className="w-12 h-12 bg-red-200 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Collected Kit Payments</p>
              <p className="text-2xl font-bold text-blue-700 mt-2">₹{kitFinancialData.collectedKitPayments.toFixed(2)}</p>
              <p className="text-xs text-blue-500 mt-1">Total Received</p>
            </div>
            <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-purple-50 to-violet-100 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium">Royalty Due</p>
              <p className="text-2xl font-bold text-purple-700 mt-2">₹{kitFinancialData.royaltyDue.toFixed(2)}</p>
              <p className="text-xs text-purple-500 mt-1">From Paid Invoices</p>
            </div>
            <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-orange-50 to-amber-100 border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">Pending Kit Payments</p>
              <p className="text-2xl font-bold text-orange-700 mt-2">{kitFinancialData.pendingKitPayments}</p>
              <p className="text-xs text-orange-500 mt-1">Kits Awaiting Payment</p>
            </div>
            <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600 font-medium">Royalty Pending</p>
              <p className="text-2xl font-bold text-yellow-700 mt-2">₹{kitFinancialData.royaltyPending.toFixed(2)}</p>
              <p className="text-xs text-yellow-500 mt-1">From Pending Invoices</p>
            </div>
            <div className="w-12 h-12 bg-yellow-200 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Fees Management
          </h3>
          <div className="flex gap-3">
            <Button 
              onClick={handleBulkGenerate} 
              className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700" 
              disabled={isBulkGenerating}
            >
              <FileText className="w-4 h-4" />
              {isBulkGenerating ? 'Generating...' : 'Generate Monthly Invoices'}
            </Button>
            <Button onClick={() => setIsInvoiceDialogOpen(true)} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Create Invoice
            </Button>
          </div>
        </div>

        <Tabs defaultValue="pending" className="w-full">
          <TabsList>
            <TabsTrigger value="pending">Pending ({pendingInvoices.length})</TabsTrigger>
            <TabsTrigger value="paid">Paid ({paidInvoices.length})</TabsTrigger>
            <TabsTrigger value="history">Payment History</TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="space-y-4 mt-4">
            {pendingInvoices.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No pending invoices</p>
            ) : (
              pendingInvoices.map(invoice => (
                <Card key={invoice.id} className="p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold">{invoice.student_name}</h4>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {invoice.student_mode.toUpperCase()}
                        </span>
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                          {invoice.fee_frequency.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>Invoice: #{invoice.invoice_number}</p>
                        <p>Amount: ₹{invoice.amount}</p>
                        <p>Due Date: {new Date(invoice.due_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handleViewInvoice(invoice)} 
                        variant="outline" 
                        className="flex items-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </Button>
                      <Button onClick={() => openPaymentDialog(invoice)} className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        Collect Payment
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="paid" className="space-y-4 mt-4">
            {paidInvoices.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No paid invoices</p>
            ) : (
              paidInvoices.map(invoice => (
                <Card key={invoice.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold">{invoice.student_name}</h4>
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                          PAID
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>Invoice: #{invoice.invoice_number}</p>
                        <p>Amount: ₹{invoice.amount}</p>
                        <p>Royalty: ₹{invoice.royalty_amount}</p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => handleViewInvoice(invoice)} 
                      variant="outline" 
                      className="flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </Button>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-4 mt-4">
            {payments.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No payment history</p>
            ) : (
              payments.map(payment => (
                <Card key={payment.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold">{payment.student_name}</h4>
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                          {payment.payment_method.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>Invoice: #{payment.invoice_number}</p>
                        <p>Amount: ₹{payment.amount}</p>
                        <p>Date: {new Date(payment.payment_date).toLocaleDateString()}</p>
                        {payment.transaction_id && <p>Transaction ID: {payment.transaction_id}</p>}
                      </div>
                    </div>
                    <Button 
                      onClick={() => handleViewReceipt(payment)} 
                      variant="outline" 
                      className="flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View Receipt
                    </Button>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </Card>

      {/* Create Invoice Dialog */}
      <Dialog open={isInvoiceDialogOpen} onOpenChange={setIsInvoiceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Fee Invoice</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateInvoice} className="space-y-4">
            <div>
              <Label htmlFor="student_id">Student *</Label>
              <select
                id="student_id"
                value={invoiceFormData.student_id}
                onChange={(e) => setInvoiceFormData({ ...invoiceFormData, student_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
                required
              >
                <option value="">Select Student</option>
                {students.map(student => (
                  <option key={student.id} value={student.id}>{student.name}</option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="student_mode">Student Mode *</Label>
              <select
                id="student_mode"
                value={invoiceFormData.student_mode}
                onChange={(e) => setInvoiceFormData({ ...invoiceFormData, student_mode: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
                required
              >
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </select>
            </div>

            <div>
              <Label htmlFor="fee_frequency">Fee Frequency *</Label>
              <select
                id="fee_frequency"
                value={invoiceFormData.fee_frequency}
                onChange={(e) => setInvoiceFormData({ ...invoiceFormData, fee_frequency: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
                required
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>

            <div>
              <Label htmlFor="due_date">Due Date *</Label>
              <Input
                id="due_date"
                type="date"
                value={invoiceFormData.due_date}
                onChange={(e) => setInvoiceFormData({ ...invoiceFormData, due_date: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                value={invoiceFormData.notes}
                onChange={(e) => setInvoiceFormData({ ...invoiceFormData, notes: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
                rows="2"
              />
            </div>

            <div className="flex gap-2">
              <Button type="submit" className="flex-1">Create Invoice</Button>
              <Button type="button" variant="outline" onClick={() => setIsInvoiceDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="mb-4 p-3 bg-gray-50 rounded">
              <p className="text-sm"><strong>Student:</strong> {selectedInvoice.student_name}</p>
              <p className="text-sm"><strong>Invoice:</strong> #{selectedInvoice.invoice_number}</p>
              <p className="text-sm"><strong>Amount:</strong> ₹{selectedInvoice.amount}</p>
            </div>
          )}
          <form onSubmit={handleRecordPayment} className="space-y-4">
            <div>
              <Label htmlFor="amount">Amount *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={paymentFormData.amount}
                onChange={(e) => setPaymentFormData({ ...paymentFormData, amount: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="payment_method">Payment Method *</Label>
              <select
                id="payment_method"
                value={paymentFormData.payment_method}
                onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_method: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
                required
              >
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>

            <div>
              <Label htmlFor="payment_date">Payment Date *</Label>
              <Input
                id="payment_date"
                type="date"
                value={paymentFormData.payment_date}
                onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_date: e.target.value })}
                required
              />
            </div>

            {paymentFormData.payment_method === 'upi' && (
              <div>
                <Label htmlFor="transaction_id">Transaction ID</Label>
                <Input
                  id="transaction_id"
                  value={paymentFormData.transaction_id}
                  onChange={(e) => setPaymentFormData({ ...paymentFormData, transaction_id: e.target.value })}
                />
              </div>
            )}

            {paymentFormData.payment_method === 'cheque' && (
              <div>
                <Label htmlFor="cheque_number">Cheque Number</Label>
                <Input
                  id="cheque_number"
                  value={paymentFormData.cheque_number}
                  onChange={(e) => setPaymentFormData({ ...paymentFormData, cheque_number: e.target.value })}
                />
              </div>
            )}

            <div>
              <Label htmlFor="payment_notes">Notes</Label>
              <textarea
                id="payment_notes"
                value={paymentFormData.notes}
                onChange={(e) => setPaymentFormData({ ...paymentFormData, notes: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
                rows="2"
              />
            </div>

            <div className="flex gap-2">
              <Button type="submit" className="flex-1">Record Payment</Button>
              <Button type="button" variant="outline" onClick={() => setIsPaymentDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Generation Result Dialog */}
      <Dialog open={showBulkResultDialog} onOpenChange={setShowBulkResultDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Invoice Generation Results</DialogTitle>
          </DialogHeader>
          {bulkResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-4 bg-blue-50">
                  <p className="text-sm text-gray-600">Generated For</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {getMonthName(bulkResult.generated_for_month)} {bulkResult.generated_for_year}
                  </p>
                </Card>
                <Card className="p-4 bg-purple-50">
                  <p className="text-sm text-gray-600">Total Students</p>
                  <p className="text-2xl font-bold text-purple-600">{bulkResult.total_students}</p>
                </Card>
                <Card className="p-4 bg-green-50">
                  <p className="text-sm text-gray-600">Invoices Generated</p>
                  <p className="text-2xl font-bold text-green-600">{bulkResult.invoices_generated}</p>
                </Card>
                <Card className="p-4 bg-yellow-50">
                  <p className="text-sm text-gray-600">Skipped</p>
                  <p className="text-2xl font-bold text-yellow-600">{bulkResult.invoices_skipped}</p>
                </Card>
              </div>

              {bulkResult.errors && bulkResult.errors.length > 0 && (
                <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 mb-2">Errors ({bulkResult.errors.length})</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {bulkResult.errors.map((error, idx) => (
                      <div key={idx} className="text-sm text-red-700 bg-white p-2 rounded">
                        <span className="font-medium">{error.student_name || error.student_id}:</span> {error.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {bulkResult.details && bulkResult.details.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Generation Details</h4>
                  <div className="border rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left">Student</th>
                          <th className="px-4 py-2 text-left">Status</th>
                          <th className="px-4 py-2 text-left">Invoice #</th>
                          <th className="px-4 py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkResult.details.map((detail, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-4 py-2">{detail.student_name}</td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-1 rounded text-xs ${
                                detail.status === 'generated' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {detail.status}
                              </span>
                            </td>
                            <td className="px-4 py-2">{detail.invoice_number || '-'}</td>
                            <td className="px-4 py-2 text-right">
                              {detail.amount ? `₹${detail.amount}` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button onClick={() => setShowBulkResultDialog(false)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice Viewer */}
      <InvoiceViewer 
        invoice={selectedInvoiceForView} 
        isOpen={isInvoiceViewerOpen} 
        onClose={() => {
          setIsInvoiceViewerOpen(false);
          setSelectedInvoiceForView(null);
        }} 
      />

      {/* Receipt Viewer */}
      <ReceiptViewer 
        payment={selectedReceiptForView} 
        isOpen={isReceiptViewerOpen} 
        onClose={() => {
          setIsReceiptViewerOpen(false);
          setSelectedReceiptForView(null);
        }} 
      />
    </div>
  );
};

export default FeesManagement;
